/**
 * StatsCard Component
 *
 * Standardized metric display card with:
 * - Title and value display
 * - Optional trend indicator (up/down with percentage)
 * - Optional icon
 * - Loading skeleton
 * - Multiple format options (number, currency, percentage)
 */

import { ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface StatsCardProps {
  title: string
  value: string | number
  change?: {
    value: number
    trend: 'up' | 'down'
    period?: string
  }
  icon?: ReactNode
  loading?: boolean
  format?: 'number' | 'currency' | 'percentage' | 'duration'
  description?: string
  className?: string
}

// Format value based on format type
function formatValue(value: string | number, format?: string): string {
  if (typeof value === 'string') return value

  switch (format) {
    case 'number':
      return new Intl.NumberFormat('en-US').format(value)
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    case 'percentage':
      return `${value.toFixed(1)}%`
    case 'duration':
      // Format as duration (e.g., "2.5h", "30m")
      if (value >= 60) {
        return `${(value / 60).toFixed(1)}h`
      }
      return `${value}m`
    default:
      return value.toString()
  }
}

export function StatsCard({
  title,
  value,
  change,
  icon,
  loading = false,
  format,
  description,
  className,
}: StatsCardProps) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4 rounded" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-3 w-40" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon && <div className="text-muted-foreground">{icon}</div>}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formatValue(value, format)}</div>

        {/* Trend indicator */}
        {change && (
          <div className="flex items-center gap-1 mt-1">
            <span
              className={cn(
                'inline-flex items-center gap-1 text-xs font-medium',
                change.trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}
            >
              {change.trend === 'up' ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              {Math.abs(change.value).toFixed(1)}%
            </span>
            {change.period && <span className="text-xs text-muted-foreground">from {change.period}</span>}
          </div>
        )}

        {/* Description */}
        {description && !change && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  )
}
